import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, Timer } from 'lucide-react';
import { swrGet, thumbUrl } from '../lib/api';
import { t } from '../lib/i18n';
import { sponsorTheme } from '../lib/liveThemes';
import { trackSponsor } from '../lib/sponsorTrack';
import LiveScene from './LiveScene';
import LiveCountdown from './LiveCountdown';

export const useLiveEvent = () => {
  const [event, setEvent] = useState(() => swrGet('/live').cached?.event || null);
  useEffect(() => { swrGet('/live').promise.then((d) => setEvent(d?.event || null)).catch(() => {}); }, []);
  return event;
};

const LiveBanner = () => {
  const ev = useLiveEvent();
  useEffect(() => { if (ev?.sponsor) trackSponsor(ev.slug, 'impression', 'banner'); }, [ev]);
  if (!ev) return null;
  const th = sponsorTheme(ev);
  const br = ev.sponsor?.branding;
  const name = ev.name;
  const tagline = ev.tagline;
  const live = ev.mode === 'live';
  return (
    <Link to={`/live/${ev.slug}`} data-testid="live-banner" onClick={() => ev.sponsor && trackSponsor(ev.slug, 'click', 'banner')} className="live-theme group relative block rounded-2xl overflow-hidden mb-6 border border-white/10 shadow-[0_20px_60px_-20px_rgba(0,0,0,.8)]" style={{ background: th.bg }}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: th.pattern }} />
      <LiveScene theme={ev.theme} accent={th.accent} compact />
      <div className="relative flex flex-col md:flex-row md:items-center gap-4 px-5 py-5 md:px-8 md:py-6">
        <div className="flex-1 min-w-0">
          <p className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.2em]" style={{ color: th.accent }} data-testid="live-banner-mode">
            <span className={`w-2 h-2 rounded-full ${live ? 'live-dot bg-red-500 text-red-500' : 'bg-white/60'}`} data-testid="live-banner-dot" /> {live ? t('DealRun Live') : t('Bientôt sur DealRun Live')}
          </p>
          <h2 className="text-2xl md:text-4xl font-black text-white mt-1 tracking-tight" data-testid="live-banner-title">{name}</h2>
          {tagline && <p className="text-sm text-white/80 mt-1 max-w-xl">{tagline}</p>}
          {ev.sponsor && (
            <div className={`mt-3 flex items-center gap-3 w-full md:w-auto md:inline-flex rounded-xl backdrop-blur border px-3 py-2 ${th.branded ? 'border-white/25' : 'bg-white/10 border-white/15 md:py-1.5'}`} style={th.branded ? { background: th.accent, color: th.on } : undefined} data-testid="live-banner-sponsor">
              <span className={`text-[10px] md:text-xs uppercase tracking-[0.18em] font-black shrink-0 ${th.branded ? 'opacity-80' : 'text-white/70'}`}>{t('Présenté par')}</span>
              {ev.sponsor.logo && <span className={`${th.branded ? 'h-12 md:h-12' : 'h-10 md:h-7'} px-2 rounded-lg bg-white flex items-center shrink-0`}><img src={thumbUrl(ev.sponsor.logo)} alt={ev.sponsor.name} className={`${th.branded ? 'h-10 max-w-[160px]' : 'h-8 md:h-5 max-w-[120px]'} w-auto object-contain`} /></span>}
              <span className={`font-black leading-tight min-w-0 break-words ${th.branded ? 'text-base' : 'text-sm text-white'}`}>{ev.sponsor.name}{th.branded && br?.headline && <span className="block text-xs font-semibold opacity-85">{br.headline}</span>}</span>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 md:gap-5 shrink-0">
          <div data-testid="live-banner-countdown">
            <p className="text-[10px] uppercase tracking-wider text-white/70 inline-flex items-center gap-1 mb-1"><Timer size={10} /> {live ? t('Se termine dans') : t('Commence dans')}</p>
            <LiveCountdown ev={ev} testId="live-banner-timer" />
          </div>
          {live && <div className="text-center px-4 py-2 rounded-xl bg-black/30 backdrop-blur border border-white/10 hidden sm:block" data-testid="live-banner-deals"><p className="text-3xl font-black text-white leading-none">{ev.deal_count}</p><p className="text-[10px] uppercase tracking-wider text-white/70">{t('meilleures offres')}</p></div>}
          <span className="live-cta inline-flex items-center gap-2 h-11 px-5 rounded-full font-bold text-sm text-black transition-transform group-hover:translate-x-1 shadow-lg" style={{ background: th.accent, color: th.on }}>
            <Sparkles size={15} /> {live ? t('Voir l\'événement') : t('En savoir plus')} <ArrowRight size={15} className="rtl:-scale-x-100" />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default LiveBanner;
